import { captureException } from '@sentry/minimal';
import { useChatHeight } from 'components/chat/hooks/UseChatHeight';
import { useUserState } from 'components/chat/hooks/UseGlobalState';
import Avatar from 'components/common/Avatar';
import ContentLayout from 'components/common/ContentLayout';
import { AddGroup } from 'components/icons/AddGroup';
import { api } from 'lib/api';
import { useStore } from 'lib/store';
import { Group, User } from 'lib/types';
import { useLogin } from 'lib/utils';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

const ROW_HEIGHT = 80;

const GroupsWrapper = styled.div<{ roundTopCorners: boolean; chatHeight: string }>((props) => ({
    width: '100%',
    background: '#fff',
    marginTop: '16px',
    border: '1px solid #e9eef2',
    borderTopLeftRadius: `${props.roundTopCorners ? '5px' : '0'}`,
    borderTopRightRadius: `${props.roundTopCorners ? '5px' : '0'}`,
    overflowY: 'scroll',
    '-webkit-overflow-scrolling': 'touch',
    '@media(max-width: 600px)': {
        marginTop: 0,
    },
    maxWidth: 640,
    margin: '0 auto',
    height: props.chatHeight,
}));

const PhotoWrapper = styled.div<{ backgroundColor?: string }>((props) => ({
    height: 64,
    width: 64,
    borderRadius: 12,
    marginRight: 12,
    float: 'left',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: props.backgroundColor ? props.backgroundColor : '#F1F0F0',
}));

const Photo = styled.img({
    width: 64,
    height: 64,
    borderRadius: 12,
    objectFit: 'cover',
});

const FlightPhoto = styled.div({
    height: 64,
    width: 64,
    display: 'block',
    background: '#F1F0F0',
    borderRadius: 12,
    lineHeight: '22px',
    '> *': {
        textAlign: 'center',
        verticalAlign: 'middle',
    },
});

const FlightPhotoRow = styled.div((props) => ({
    color: '#000000',
    fontFamily: props.theme.fonts.default,
    fontWeight: 400,
}));

const IconRow = styled.div({
    display: 'flex',
    lineHeight: '24px',
    fontSize: 24,
    alignSelf: 'center',
});

const renderGroupPhoto = (group: Group) => {
    if (group.photo !== '') {
        return <Photo src={group.photo} />;
    } else if (group.flight?.from && group.flight?.to) {
        return (
            <FlightPhoto>
                <FlightPhotoRow>{group.flight.from}</FlightPhotoRow>
                <FlightPhotoRow>&#9992;&#65039;</FlightPhotoRow>
                <FlightPhotoRow>{group.flight.to}</FlightPhotoRow>
            </FlightPhoto>
        );
    } else {
        return <IconRow>🏖️</IconRow>;
    }
};

const GroupName = styled.div<{ addBorder: boolean; bold?: boolean }>(
    ({ addBorder, bold, theme }) => ({
        fontFamily: theme.fonts.default,
        fontWeight: bold ? 500 : 400,
        size: 16,
        alignItems: 'center',
        display: 'inline-flex',
        lineHeight: '80px',
        float: 'left',
        height: 80,
        borderBottom: addBorder ? '1px solid #E9EEF2' : 0,
        width: 'calc(100% - 88px)',
        overflow: 'hidden',
        color: '#000000',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    })
);

const GroupRow = styled.div({
    background: '#FFFFFF',
    height: ROW_HEIGHT,
    paddingLeft: 8,
    paddingRight: 8,
    '&:hover,active,focus,link,visited': {
        background: '#E9EEF2',
    },
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
});

const UnreadBubble = styled.div({
    height: 12,
    width: 12,
    background: '#EC4747',
    borderRadius: 12,
    float: 'right',
});

const GroupLink = styled.a({
    textDecoration: 'none',
    cursor: 'pointer',
    userSelect: 'none',
});

const renderGroupRows = (groups: Array<Group>, user?: User) => {
    return groups
        .filter((v) => v.id !== user?.id)
        .sort((a, b) => {
            if (a.id === user?.id) {
                return -1;
            } else if (b.id === user?.id) {
                return 1;
            }
            return (
                DateTime.fromISO(b.last_message_at).toMillis() -
                DateTime.fromISO(a.last_message_at).toMillis()
            );
        })
        .map((group, index) => {
            return (
                <Link key={group.id} href={`/groups/${group.id}`} passHref>
                    <GroupLink>
                        <GroupRow>
                            <PhotoWrapper>{renderGroupPhoto(group)}</PhotoWrapper>
                            <GroupName addBorder={index !== groups.length - 1}>
                                {group.name}
                            </GroupName>
                            {DateTime.fromISO(group.last_message_at) >
                            DateTime.fromISO(group.last_message_read_at) ? (
                                <UnreadBubble />
                            ) : null}
                        </GroupRow>
                    </GroupLink>
                </Link>
            );
        });
};

const HomePage: React.FC = () => {
    const theme = useTheme();
    const router = useRouter();
    const user = useUserState((state) => state.user);
    const login = useLogin();
    const { t } = useTranslation('page');
    let userGroups = useUserState((state) => state.groups);
    const updateUserGroups = useUserState((state) => state.updateUserGroups);
    useEffect(() => {
        updateUserGroups();
    }, []);

    useEffect(() => {
        if (!user) {
            login();
        }
    }, [user]);
    const chatHeight = useChatHeight();
    let groupWrapperHeight = (userGroups.length + 1) * ROW_HEIGHT + 2;
    if (groupWrapperHeight > chatHeight) {
        groupWrapperHeight = chatHeight;
    }

    const createNewGroupAction = useCallback(async () => {
        try {
            if (!user) {
                await login();
            } else if (user.guest) {
                router.push(`/login?redirect_path=/home`);
                return;
            }

            const { data: newGroup } = await api.createGroup();
            router.push(`/groups/${newGroup.id}`);
        } catch (err) {
            captureException(err);
        }
    }, [user]);

    const isWidget = useStore((store) => store.isWidget);
    const botName = useStore((store) => store.config.botName);

    return (
        <ContentLayout>
            <GroupsWrapper roundTopCorners={!isWidget} chatHeight={`${groupWrapperHeight}px`}>
                <Link href="/" passHref>
                    <GroupLink>
                        <GroupRow>
                            <PhotoWrapper backgroundColor={theme.colors.background}>
                                <Avatar
                                    css={{
                                        height: 46,
                                        width: 49,
                                        display: 'block',
                                        margin: 'auto',
                                        verticalAlign: 'middle',
                                    }}
                                />
                            </PhotoWrapper>
                            <GroupName addBorder={true} bold={true}>
                                <b>{botName}</b>
                            </GroupName>
                        </GroupRow>
                    </GroupLink>
                </Link>
                <GroupLink onClick={createNewGroupAction}>
                    <GroupRow>
                        <PhotoWrapper backgroundColor={theme.colors.primary}>
                            <AddGroup
                                css={{
                                    height: 46,
                                    width: 49,
                                    display: 'block',
                                    margin: 'auto',
                                    verticalAlign: 'middle',
                                }}
                            />
                        </PhotoWrapper>
                        <GroupName addBorder={true} bold={true}>
                            {t('home.create-new-group')}
                        </GroupName>
                    </GroupRow>
                </GroupLink>
                {renderGroupRows(userGroups, user)}
            </GroupsWrapper>
        </ContentLayout>
    );
};

export default HomePage;
